import { Fragment, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Table } from 'react-bootstrap'
import {
  approveMasterTerm,
  bulkImportMasterTerms,
  createMasterTerm,
  deleteMasterTerm,
  fetchMasterTermHistory,
  fetchMasterTerms,
  getMasterTermsExportUrl,
  promoteMasterTermFamilyExtension,
  retireMasterTerm,
  updateMasterTerm,
  type MasterTermHistoryDto,
  type UpsertMasterTermRequest,
  type MasterTermDto,
} from '../services/api'
import { useAuth } from '../context/AuthContext'

type EditFormState = {
  canonicalTerm: string
  category: string
  dataType: string
  description: string
  aliasesCsv: string
  translationsCsv: string
  productFamiliesCsv: string
}

const EMPTY_FORM: EditFormState = {
  canonicalTerm: '',
  category: 'Metadata',
  dataType: 'Text',
  description: '',
  aliasesCsv: '',
  translationsCsv: '',
  productFamiliesCsv: '',
}

export default function MasterTerms() {
  const { user } = useAuth()
  const [rows, setRows] = useState<MasterTermDto[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [searchFilter, setSearchFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [languageFilter, setLanguageFilter] = useState('')
  const [familyFilter, setFamilyFilter] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<EditFormState>(EMPTY_FORM)
  const [importJson, setImportJson] = useState('')
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const [historyRows, setHistoryRows] = useState<MasterTermHistoryDto[]>([])
  const [historyTermId, setHistoryTermId] = useState<string | null>(null)
  const [showTermModal, setShowTermModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)

  const canPropose = useMemo(() => {
    const roles = (user?.functionalRoles ?? []).map((r: string) => r.toLowerCase())
    const isMasterAdmin = ((user as { businessLevel?: string })?.businessLevel ?? '').toLowerCase() === 'masteradmin'
    return isMasterAdmin || roles.includes('author') || roles.includes('publisher') || roles.includes('usermanagement')
  }, [user])

  const canApprove = useMemo(() => {
    const roles = (user?.functionalRoles ?? []).map((r: string) => r.toLowerCase())
    const isMasterAdmin = ((user as { businessLevel?: string })?.businessLevel ?? '').toLowerCase() === 'masteradmin'
    return isMasterAdmin || roles.includes('publisher')
  }, [user])

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await fetchMasterTerms({
        search: searchFilter || undefined,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
        languageCode: languageFilter || undefined,
        productFamily: familyFilter || undefined,
      })
      setRows(data)
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Failed to load master terms.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const startCreate = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setShowTermModal(true)
  }

  const startEdit = (row: MasterTermDto) => {
    setEditingId(row.id)
    setForm({
      canonicalTerm: row.canonicalTerm,
      category: row.category,
      dataType: row.dataType,
      description: row.description ?? '',
      aliasesCsv: row.aliases.map((a) => (a.languageCode ? `${a.languageCode}:${a.alias}` : a.alias)).join(', '),
      translationsCsv: row.translations.map((t) => `${t.languageCode}:${t.localizedTerm}`).join(', '),
      productFamiliesCsv: row.productFamilies.map((f) => f.productFamily).join(', '),
    })
    setShowTermModal(true)
  }

  const parseRequestFromForm = (): UpsertMasterTermRequest => {
    const aliases = form.aliasesCsv
      .split(',')
      .map((a) => a.trim())
      .filter(Boolean)
      .map((value) => {
        const firstColon = value.indexOf(':')
        if (firstColon <= 0)
          return { alias: value }
        const languageCode = value.slice(0, firstColon).trim()
        const alias = value.slice(firstColon + 1).trim()
        return { languageCode, alias }
      })

    const translations = form.translationsCsv
      .split(',')
      .map((a) => a.trim())
      .filter(Boolean)
      .map((value) => {
        const firstColon = value.indexOf(':')
        if (firstColon <= 0)
          return null
        const languageCode = value.slice(0, firstColon).trim()
        const localizedTerm = value.slice(firstColon + 1).trim()
        if (!languageCode || !localizedTerm) return null
        return { languageCode, localizedTerm, isPreferred: true }
      })
      .filter(Boolean) as Array<{ languageCode: string; localizedTerm: string; isPreferred: boolean }>

    const productFamilies = form.productFamiliesCsv
      .split(',')
      .map((a) => a.trim())
      .filter(Boolean)
      .map((productFamily) => ({
        productFamily,
        status: 'Active',
        isFamilyExtension: false,
      }))

    return {
      canonicalTerm: form.canonicalTerm.trim(),
      category: form.category.trim(),
      dataType: form.dataType.trim(),
      description: form.description.trim() || undefined,
      aliases: aliases.length ? aliases : undefined,
      translations: translations.length ? translations : undefined,
      productFamilies: productFamilies.length ? productFamilies : undefined,
    }
  }

  const save = async () => {
    if (!canPropose) {
      setError('You do not have permission to propose/edit terms.')
      return
    }
    if (!form.canonicalTerm.trim()) {
      setError('Canonical term is required.')
      return
    }

    setBusyAction('save')
    setError('')
    try {
      const request = parseRequestFromForm()
      if (editingId) await updateMasterTerm(editingId, request)
      else await createMasterTerm(request)
      setEditingId(null)
      setForm(EMPTY_FORM)
      setShowTermModal(false)
      await load()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Save failed.')
    } finally {
      setBusyAction(null)
    }
  }

  const approve = async (id: string) => {
    setBusyAction(`approve-${id}`)
    setError('')
    try {
      await approveMasterTerm(id)
      await load()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Approve failed.')
    } finally {
      setBusyAction(null)
    }
  }

  const retire = async (id: string) => {
    setBusyAction(`retire-${id}`)
    setError('')
    try {
      await retireMasterTerm(id)
      await load()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Retire failed.')
    } finally {
      setBusyAction(null)
    }
  }

  const remove = async (id: string) => {
    if (!confirm('Delete this master term?'))
      return
    setBusyAction(`delete-${id}`)
    setError('')
    try {
      await deleteMasterTerm(id)
      await load()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Delete failed.')
    } finally {
      setBusyAction(null)
    }
  }

  const promote = async (id: string, productFamily: string) => {
    setBusyAction(`promote-${id}-${productFamily}`)
    setError('')
    try {
      await promoteMasterTermFamilyExtension(id, { productFamily })
      await load()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Promotion failed.')
    } finally {
      setBusyAction(null)
    }
  }

  const openHistory = async (id: string) => {
    setBusyAction(`history-${id}`)
    setError('')
    try {
      const history = await fetchMasterTermHistory(id)
      setHistoryRows(history)
      setHistoryTermId(id)
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Failed to load history.')
    } finally {
      setBusyAction(null)
    }
  }

  const importTerms = async () => {
    if (!canPropose) {
      setError('You do not have permission to import terms.')
      return
    }
    setBusyAction('import')
    setError('')
    try {
      const payload = JSON.parse(importJson)
      const terms = Array.isArray(payload) ? payload : payload.terms
      if (!Array.isArray(terms))
        throw new Error('JSON must be an array of terms or { terms: [] }.')
      await bulkImportMasterTerms({ terms })
      setImportJson('')
      setShowImportModal(false)
      await load()
    } catch (err: any) {
      setError(err.response?.data ?? err.message ?? 'Import failed.')
    } finally {
      setBusyAction(null)
    }
  }

  const renderCompactList = (items: string[], visibleCount = 2) => {
    if (!items || items.length === 0) return '-'
    const visible = items.slice(0, visibleCount)
    const hidden = items.length - visible.length
    const full = items.join(', ')
    return (
      <div title={full}>
        <div className="small text-truncate" style={{ maxWidth: 340 }}>
          {visible.join(', ')}
          {hidden > 0 ? ` +${hidden} more` : ''}
        </div>
      </div>
    )
  }

  return (
    <Container className="py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="h3 mb-1">Master terms</h1>
          <p className="text-muted mb-0">Organisation-wide glossary of approved terms, translations, and aliases. Used for consistent language across markets. PIM categories can import from Master terms for product-family-scoped mappings.</p>
        </div>
        <div className="d-flex gap-2">
          <Button type="button" variant="outline-secondary" as="a" href={getMasterTermsExportUrl('csv')} target="_blank" rel="noreferrer">
            Export CSV
          </Button>
          {canPropose && (
            <Button type="button" variant="outline-secondary" onClick={() => setShowImportModal(true)}>
              Import
            </Button>
          )}
          <Button type="button" onClick={startCreate} disabled={!canPropose}>New term</Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label>Search</Form.Label>
              <Form.Control value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} placeholder="Term or alias" />
            </Col>
            <Col md={2}>
              <Form.Label>Status</Form.Label>
              <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="Draft">Draft</option>
                <option value="Approved">Approved</option>
                <option value="Retired">Retired</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Category</Form.Label>
              <Form.Control value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} placeholder="Metadata" />
            </Col>
            <Col md={2}>
              <Form.Label>Language</Form.Label>
              <Form.Control value={languageFilter} onChange={(e) => setLanguageFilter(e.target.value)} placeholder="en" />
            </Col>
            <Col md={2}>
              <Form.Label>Family</Form.Label>
              <Form.Control value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)} placeholder="Insulation" />
            </Col>
          </Row>
          <div className="mt-2 d-flex justify-content-end">
            <Button type="button" variant="outline-primary" onClick={load} disabled={loading}>Apply filters</Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-muted">Loading master terms...</div>
          ) : rows.length === 0 ? (
            <div className="text-muted">No terms found.</div>
          ) : (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th style={{ width: 220 }}>Canonical term</th>
                  <th style={{ width: 120 }}>Category</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 340 }}>Aliases</th>
                  <th style={{ width: 260 }}>Translations</th>
                  <th style={{ width: 160 }}>Families</th>
                  <th style={{ width: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Fragment key={row.id}>
                  <tr>
                    <td>
                      <div>{row.canonicalTerm}</div>
                      {row.description && <small className="text-muted">{row.description}</small>}
                    </td>
                    <td>{row.category}</td>
                    <td>
                      <Badge bg={row.status === 'Approved' ? 'success' : row.status === 'Draft' ? 'secondary' : 'warning'}>
                        {row.status}
                      </Badge>
                    </td>
                    <td>{renderCompactList(row.aliases.map((a) => a.languageCode ? `${a.languageCode}:${a.alias}` : a.alias), 2)}</td>
                    <td>{renderCompactList(row.translations.map((t) => `${t.languageCode}:${t.localizedTerm}`), 2)}</td>
                    <td>{renderCompactList(row.productFamilies.map((f) => f.productFamily), 2)}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1 justify-content-end">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="py-0 px-2"
                        onClick={() => openHistory(row.id)}
                        disabled={busyAction === `history-${row.id}`}
                      >
                        History
                      </Button>
                      {canPropose && (
                        <Button size="sm" variant="outline-secondary" className="py-0 px-2" onClick={() => startEdit(row)}>
                          Edit
                        </Button>
                      )}
                      {canApprove && row.status !== 'Approved' && (
                        <Button
                          size="sm"
                          variant="outline-success"
                          className="py-0 px-2"
                          onClick={() => approve(row.id)}
                          disabled={busyAction === `approve-${row.id}`}
                        >
                          Approve
                        </Button>
                      )}
                      {canApprove && row.status !== 'Retired' && (
                        <Button
                          size="sm"
                          variant="outline-warning"
                          className="py-0 px-2"
                          onClick={() => retire(row.id)}
                          disabled={busyAction === `retire-${row.id}`}
                        >
                          Retire
                        </Button>
                      )}
                      {canApprove && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          className="py-0 px-2"
                          onClick={() => remove(row.id)}
                          disabled={busyAction === `delete-${row.id}`}
                        >
                          Delete
                        </Button>
                      )}
                      </div>
                    </td>
                  </tr>
                  {canApprove && row.productFamilies.some((f) => f.isFamilyExtension) && (
                    <tr>
                      <td colSpan={7}>
                        <div className="small d-flex flex-wrap align-items-center gap-2">
                          <span className="text-muted">Family extension(s) pending promotion:</span>
                          {row.productFamilies.filter((f) => f.isFamilyExtension).map((f) => (
                            <Button
                              key={`${row.id}-${f.productFamily}`}
                              size="sm"
                              variant="outline-success"
                              onClick={() => promote(row.id, f.productFamily)}
                              disabled={busyAction === `promote-${row.id}-${f.productFamily}`}
                            >
                              Promote {f.productFamily}
                            </Button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {historyTermId && (
        <Card className="mt-3">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h2 className="h6 mb-0">Term history</h2>
              <Button type="button" size="sm" variant="outline-secondary" onClick={() => { setHistoryRows([]); setHistoryTermId(null) }}>
                Close
              </Button>
            </div>
            {historyRows.length === 0 ? (
              <div className="text-muted small">No audit history found.</div>
            ) : (
              <Table size="sm" striped responsive>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((h) => (
                    <tr key={h.id}>
                      <td>{new Date(h.actionUtc).toLocaleString()}</td>
                      <td>{h.actor}</td>
                      <td>{h.actionType}</td>
                      <td>{h.description ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      <Modal show={showTermModal} onHide={() => setShowTermModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Edit term' : 'Create term'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-2">
            <Col md={6}>
              <Form.Label>Canonical term</Form.Label>
              <Form.Control value={form.canonicalTerm} onChange={(e) => setForm({ ...form, canonicalTerm: e.target.value })} />
            </Col>
            <Col md={3}>
              <Form.Label>Category</Form.Label>
              <Form.Control value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </Col>
            <Col md={3}>
              <Form.Label>Data type</Form.Label>
              <Form.Control value={form.dataType} onChange={(e) => setForm({ ...form, dataType: e.target.value })} />
            </Col>
            <Col md={12}>
              <Form.Label>Product families (csv)</Form.Label>
              <Form.Control value={form.productFamiliesCsv} onChange={(e) => setForm({ ...form, productFamiliesCsv: e.target.value })} placeholder="Family A, Family B" />
            </Col>
            <Col md={12}>
              <Form.Label>Aliases (csv, optional lang:term)</Form.Label>
              <Form.Control value={form.aliasesCsv} onChange={(e) => setForm({ ...form, aliasesCsv: e.target.value })} placeholder="dimensions, de:abmessungen" />
            </Col>
            <Col md={12}>
              <Form.Label>Translations (csv, required lang:term)</Form.Label>
              <Form.Control value={form.translationsCsv} onChange={(e) => setForm({ ...form, translationsCsv: e.target.value })} placeholder="de:Abmessungen, fr:Dimensions" />
            </Col>
            <Col md={12}>
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" onClick={() => { setShowTermModal(false); setEditingId(null); setForm(EMPTY_FORM) }}>
            Cancel
          </Button>
          <Button type="button" onClick={save} disabled={busyAction === 'save'}>
            {editingId ? 'Save changes' : 'Create draft'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Bulk import terms</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            rows={10}
            value={importJson}
            onChange={(e) => setImportJson(e.target.value)}
            placeholder='[{"canonicalTerm":"Dimensions","category":"Technical","aliases":[{"alias":"size"}]}]'
          />
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" onClick={() => setShowImportModal(false)}>
            Cancel
          </Button>
          <Button type="button" variant="outline-primary" onClick={importTerms} disabled={busyAction === 'import'}>
            {busyAction === 'import' ? 'Importing...' : 'Import'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
